import { useState } from 'react';
import { AlertTriangle, Upload, FileText, CheckCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';


const PaymentRejectionFeedback = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock data - In real implementation, this would come from backend/context
  const rejectionData = {
    userName: 'Carlos Martínez',
    membershipTier: 'Emprendedor',
    monthlyCost: '$50.000 COP',
    benefits: [
      '50 descuentos mensuales',
      'Acceso a directorio premium',
      'Programa de referidos activo',
      'Soporte prioritario'
    ],
    adminObservations: 'El comprobante de pago enviado no se puede leer claramente. La imagen está borrosa y no se distingue el valor de la transacción ni la fecha. Por favor, tome una fotografía más nítida del comprobante asegurándose de que todos los detalles sean legibles, especialmente el monto de $50.000 COP y la fecha de la transacción.',
    rejectionDate: '2025-01-20',
    severity: 'minor',
    referenceNumber: 'REJ-2025-001234'
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    processFile(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    processFile(file);
  };

  const processFile = (file) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes?.includes(file?.type)) {
      alert('Formato no válido. Por favor, suba un archivo JPG, PNG o PDF.');
      return;
    }

    if (file?.size > maxSize) {
      alert('El archivo es demasiado grande. El tamaño máximo es 5MB.');
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file?.type?.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e?.target?.result);
      reader?.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      alert('Por favor, seleccione un archivo antes de enviar.');
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      alert('¡Comprobante enviado exitosamente! Su solicitud será revisada por el equipo de administración.');
      navigate('/user-dashboard');
    }, 2000);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const membershipColors = {
    'Explorador': 'bg-blue-100 text-blue-800 border-blue-300',
    'Emprendedor': 'bg-purple-100 text-purple-800 border-purple-300',
    'Elite': 'bg-amber-100 text-amber-800 border-amber-300'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/assets/images/LOGO-1765669087927.jpg" 
                alt="BIZZNET Logo" 
                className="h-10 w-auto"
              />
              <h1 className="text-2xl font-bold text-gray-900">BIZZNET</h1>
            </div>
            <button
              onClick={() => navigate('/user-dashboard')}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Rejection Status Banner */}
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-red-900 mb-2">
                Verificación de Pago Rechazada
              </h2>
              <p className="text-red-700 mb-2">
                Hola <span className="font-semibold">{rejectionData?.userName}</span>, tu solicitud de activación ha sido rechazada. Por favor, revisa las observaciones del administrador y envía un nuevo comprobante de pago.
              </p>
              <div className="flex items-center gap-4 text-sm text-red-600 mt-3">
                <span>Referencia: <span className="font-mono font-semibold">{rejectionData?.referenceNumber}</span></span>
                <span>Fecha: {rejectionData?.rejectionDate}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Observations */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Observaciones del Administrador
          </h3>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
            <p className="text-gray-800 leading-relaxed whitespace-pre-line">
              {rejectionData?.adminObservations}
            </p>
          </div>
        </div>

        {/* Membership Information */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Información de tu Membresía</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600 font-medium">Plan Seleccionado</label>
              <div className={`mt-1 px-4 py-2 rounded-lg border-2 inline-block ${membershipColors?.[rejectionData?.membershipTier]}`}>
                <span className="font-bold text-lg">{rejectionData?.membershipTier}</span>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-600 font-medium">Costo Mensual</label>
              <p className="text-2xl font-bold text-gray-900 mt-1">{rejectionData?.monthlyCost}</p>
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm text-gray-600 font-medium">Beneficios Incluidos</label>
            <ul className="mt-2 space-y-2">
              {rejectionData?.benefits?.map((benefit, index) => (
                <li key={index} className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Subir Nuevo Comprobante de Pago</h3>
          
          {/* Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              isDragging
                ? 'border-blue-500 bg-blue-50' :'border-gray-300 bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <input
              type="file"
              id="file-upload"
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="flex flex-col items-center gap-3">
                <div className="p-4 bg-blue-100 rounded-full">
                  <Upload className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900 mb-1">
                    Arrastra tu archivo aquí o haz clic para seleccionar
                  </p>
                  <p className="text-sm text-gray-600">
                    Formatos aceptados: JPG, PNG, PDF | Tamaño máximo: 5MB
                  </p>
                </div>
              </div>
            </label>
          </div>

          {/* File Preview */}
          {selectedFile && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-gray-900">{selectedFile?.name}</p>
                    <p className="text-sm text-gray-600">
                      {(selectedFile?.size / 1024 / 1024)?.toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewUrl(null);
                  }}
                  className="px-3 py-1 text-sm text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                >
                  Eliminar
                </button>
              </div>
              {previewUrl && (
                <div className="mt-3">
                  <img src={previewUrl} alt="Preview" className="max-h-64 rounded-lg mx-auto" />
                </div>
              )}
            </div>
          )}

          {/* Guidelines */}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Recomendaciones para el Comprobante:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Asegúrate de que la imagen sea clara y legible</li>
              <li>• Verifica que el monto coincida con el plan seleccionado ({rejectionData?.monthlyCost})</li>
              <li>• La fecha de la transacción debe ser reciente</li>
              <li>• Incluye todos los detalles de la transacción</li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate('/user-dashboard')}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedFile || isSubmitting}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Enviar para Revisión
              </>
            )}
          </button>
        </div>

        {/* Support Information */}
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
          <p className="text-sm text-gray-600">
            ¿Necesitas ayuda? Contacta a soporte en{' '}
            <a href="mailto:soporte@bizznet.com" className="text-blue-600 hover:underline font-semibold">
              soporte@bizznet.com
            </a>
            {' '}o al{' '}
            <a href="tel:+573001234567" className="text-blue-600 hover:underline font-semibold">
              +57 300 123 4567
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default PaymentRejectionFeedback;